import "react-leaflet-markercluster/dist/styles.min.css"
import "leaflet/dist/leaflet.css"
import "./MainMap.scss"

import L from "leaflet"
import { useCallback, useContext, useEffect, useRef } from "react"
import { Map } from "react-leaflet"
import MarkerClusterGroup from "react-leaflet-markercluster"
import { shallowEqual, useDispatch } from "react-redux"

import { MapContext, useAppSelector } from "@/App"
import { selectDriverById } from "@/store/drivers"
import { setAboutPanel } from "@/store/ui"
import { MapRoute } from "@/views/MapView"

import {
  ClientsSearchLayer,
  DefaultLayer,
  DeliveryZonesLayer,
  DriverTasksLayer,
  DriverTasksRouteHistoryLayer,
  OrdersSearchLayer,
  PinnedLocationLayer,
} from "./partials/FeatureGroups"
import { HereTileLayer } from "./partials/HereTileLayer"
import { Routes } from "./partials/Routes"

export const DEFAULT_MAP_CENTER: L.LatLngTuple = [52, 19]
const DEFAULT_MAP_ZOOM = 7

type MainMapProps = {
  routes: MapRoute[]
  className?: string
}

export const MainMap = ({ routes, className }: MainMapProps) => {
  const mapRef = useContext(MapContext)
  const clusterRef = useRef<MarkerClusterGroup | null>(null)

  const layers = useAppSelector((state) => state.global.mapLayers, shallowEqual)

  const dispatch = useDispatch()

  const dataFetched = useAppSelector(
    (state) =>
      state.orders.status === "fetched" && state.drivers.status === "fetched" && state.clients.status === "fetched" // TODO
  )

  const driverIdTasksRoute = useAppSelector((state) => state.ui.driverIdTasksRoute)

  const driverIdTasksRouteHistory = useAppSelector((state) => state.ui.driverIdTasksRouteHistory)

  const ordersPanelSearchValue = useAppSelector((state) => state.ui.ordersPanelSearchValue)

  const clientsSearchValue = useAppSelector((state) => state.ui.clientsSearchValue)

  const pinnedLocation = useAppSelector((state) => state.ui.pinnedLocation)

  useEffect(() => {
    if (!mapRef?.current) return

    dataFetched ? enableMapUserInteraction(mapRef) : disableMapUserInteraction(mapRef)
  }, [mapRef, dataFetched])

  useDriverDetailsFocus()

  useInitialMapBounding(clusterRef)

  useClosePopupOnClickAndEsc()

  const handlePopupOpen = useCallback(() => {
    dispatch(setAboutPanel(false))
  }, [dispatch])

  return (
    <Map
      ref={mapRef}
      className={className}
      center={DEFAULT_MAP_CENTER}
      zoom={DEFAULT_MAP_ZOOM}
      minZoom={5}
      maxZoom={22}
      zoomControl={false}
      onpopupopen={handlePopupOpen}
      closePopupOnClick={false} // Overwritten with custom event
      // preferCanvas - breaks route coloring
    >
      <HereTileLayer />
      {layers.includes("traffic") && <HereTileLayer type="traffic" />}

      <Routes routes={routes} />

      {pinnedLocation && <PinnedLocationLayer />}

      {driverIdTasksRouteHistory ? (
        <DriverTasksRouteHistoryLayer driverId={driverIdTasksRouteHistory} />
      ) : driverIdTasksRoute ? (
        <DriverTasksLayer driverId={driverIdTasksRoute} />
      ) : ordersPanelSearchValue ? (
        <OrdersSearchLayer layers={layers} />
      ) : clientsSearchValue ? (
        <ClientsSearchLayer layers={layers} />
      ) : (
        <DefaultLayer layers={layers} clusterRef={clusterRef} />
      )}

      <DeliveryZonesLayer layers={layers} />
    </Map>
  )
}

function useDriverDetailsFocus() {
  const mapRef = useContext(MapContext)

  const focusedDriverId = useAppSelector((state) => state.ui.focusedDriverId)
  const focusedOrderId = useAppSelector((state) => state.ui.focusedOrderId)
  const focusedOrderArea = useAppSelector((state) => state.ui.focusedOrderArea, shallowEqual)
  const showDriverDetails = useAppSelector((state) => state.ui.showDriverDetails)
  const isAboutPanelVisible = useAppSelector((state) => Boolean(state.ui.aboutPanel))
  const driver = useAppSelector(showDriverDetails ? selectDriverById(showDriverDetails) : () => null)

  useEffect(() => {
    if (!mapRef?.current || !focusedDriverId || showDriverDetails !== focusedDriverId || isAboutPanelVisible) return

    if (!driver || !driver.lat || !driver.lng) return

    const driverBounds = L.latLng(driver.lat, driver.lng)

    if (!focusedOrderId && focusedOrderArea && focusedOrderArea.length && !isAboutPanelVisible) {
      const bounds = L.latLngBounds([
        driverBounds,
        ...(focusedOrderArea
          .map((order) => [order.pickup?.lat, order.pickup?.lng])
          .filter((tuple) => tuple?.every((coord) => Boolean(coord))) as [number, number][]),
        ...(focusedOrderArea
          .map((order) => [order.delivery?.lat, order.delivery?.lng])
          .filter((tuple) => tuple?.every((coord) => Boolean(coord))) as [number, number][]),
      ])
      mapRef?.current.leafletElement.flyToBounds(bounds, {
        animate: false,
        paddingTopLeft: [16, 16],
        paddingBottomRight: [16, 16],
      })
    } else {
      const bounds = L.latLngBounds([driverBounds])
      mapRef?.current.leafletElement.flyToBounds(bounds, { animate: false })
    }

    // Drivers change not important
    // eslint-disable-next-line
  }, [mapRef, focusedDriverId, focusedOrderArea, showDriverDetails])
}

function useInitialMapBounding(clusterRef: React.MutableRefObject<MarkerClusterGroup | null>) {
  const mapRef = useContext(MapContext)
  const cityBounds = useAppSelector((state) => state.teams.cityBounds)

  useEffect(() => {
    if (cityBounds && mapRef?.current && clusterRef.current) {
      mapRef.current.leafletElement.flyToBounds(cityBounds, {
        animate: false,
      })
    }
  }, [mapRef, clusterRef, cityBounds])
}

function useClosePopupOnClickAndEsc() {
  const mapRef = useContext(MapContext)

  const closePopupOnClick = useCallback(
    (event) => {
      const isMarker = event.target.classList.contains("leaflet-marker-icon")
      const isPopup = event.target.closest(".leaflet-popup")

      if (mapRef?.current && !isMarker && !isPopup) {
        mapRef.current.leafletElement.closePopup()
      }
    },
    [mapRef]
  )

  const closePopupOnEsc = useCallback(
    (event) => {
      if (mapRef?.current && event.key === "Escape") {
        mapRef.current.leafletElement.closePopup()
      }
    },
    [mapRef]
  )

  useEffect(() => {
    document.addEventListener("click", closePopupOnClick)
    document.addEventListener("keydown", closePopupOnEsc)

    return () => {
      document.removeEventListener("click", closePopupOnClick)
      document.removeEventListener("keydown", closePopupOnEsc)
    }
  }, [closePopupOnClick, closePopupOnEsc])
}

function disableMapUserInteraction(mapRef: React.MutableRefObject<Map | null> | null) {
  if (!mapRef || !mapRef.current) return

  const map = mapRef.current.leafletElement
  if (map) {
    map.dragging.disable()
    map.touchZoom.disable()
    map.doubleClickZoom.disable()
    map.scrollWheelZoom.disable()
    map.boxZoom.disable()
    map.keyboard.disable()
    map.tap?.disable()
  }

  const mapContainer = mapRef.current.container
  if (mapContainer) {
    mapContainer.style.opacity = "0.25"
    mapContainer.style.zIndex = "1035"
  }
}

function enableMapUserInteraction(mapRef: React.MutableRefObject<Map | null> | null) {
  if (!mapRef || !mapRef.current) return

  const map = mapRef.current.leafletElement
  if (map) {
    map.dragging.enable()
    map.touchZoom.enable()
    map.doubleClickZoom.enable()
    map.scrollWheelZoom.enable()
    map.boxZoom.enable()
    map.keyboard.enable()
    map.tap?.enable()
  }

  const mapContainer = mapRef.current.container
  if (mapContainer) {
    mapContainer.style.opacity = "1"
    mapContainer.style.zIndex = ""
  }
}
